import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import { useNotifications } from '../hooks/useNotifications';
import LucvanHeader from '../components/LucvanHeader';

export default function ProductionDashboard() {
  const [activeTab, setActiveTab] = useState('pending')
  const [statusFilter, setStatusFilter] = useState(null) // null = mostrar todos
  const [sortByDate, setSortByDate] = useState('newest') // 'newest' o 'oldest'
  const [searchTerm, setSearchTerm] = useState('')
  const [showClosedInSearch, setShowClosedInSearch] = useState(false) // mostrar tickets cerrados en búsqueda
  const { currentUser, logout } = useAuth()
  const navigate = useNavigate()

  // Hook para notificaciones en tiempo real
  const { 
    notifications, 
    unreadCount, 
    isConnected,
    markAsRead,
    markAllAsRead,
    clearNotification
  } = useNotifications(currentUser?.id)

  const [requests, setRequests] = useState([])
  const [patients, setPatients] = useState([])
  const [clinics, setClinics] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Cargar datos
  useEffect(() => {
    const load = async () => {
      // Verificar que el usuario esté autenticado
      if (!currentUser) {
        console.log('[ProductionDashboard] Usuario no autenticado')
        setLoading(false)
        return
      }

      try {
        // Cargar solicitudes
        const resRequests = await apiFetch('/api/requests')
        if (resRequests.ok) {
          const reqData = await resRequests.json()
          setRequests(reqData || [])
        } else {
          const errorData = await resRequests.json().catch(() => ({}))
          console.error('[ProductionDashboard] Error loading requests:', resRequests.status, errorData)
          if (resRequests.status === 400) {
            setError(`Error de autenticación: ${errorData.error || 'No autorizado'}. Por favor verifica que tu usuario tenga el rol correcto.`)
          }
        }

        // Cargar pacientes
        const resPatients = await apiFetch('/api/patients')
        if (resPatients.ok) {
          const patientsData = await resPatients.json()
          setPatients(patientsData || [])
        } else {
          const errorData = await resPatients.json().catch(() => ({}))
          console.error('[ProductionDashboard] Error loading patients:', resPatients.status, errorData)
        }

        // Cargar clínicas
        const resClinics = await apiFetch('/api/clinics?limit=100')
        if (resClinics.ok) {
          const clinicsData = await resClinics.json()
          setClinics(clinicsData || [])
        }
      } catch (err) {
        console.error('Error loading data:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [currentUser])

  // Cuando llega una notificación de nueva solicitud, recargar solicitudes
  useEffect(() => {
    if (notifications.length > 0 && notifications[0].type === 'new_request') {
      const loadRequests = async () => {
        try {
          const res = await apiFetch('/api/requests')
          if (res.ok) {
            const data = await res.json()
            setRequests(data || [])
          }
        } catch (err) {
          console.error('Error loading requests:', err)
        }
      }
      loadRequests()
    }
  }, [notifications])

  // Auto-refresh: volver a cargar solicitudes cada 10s (pausado si la pestaña está oculta)
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        if (document.hidden) return
        const res = await apiFetch('/api/requests')
        if (res.ok) {
          const data = await res.json()
          setRequests(data || [])
        }
      } catch (err) {
        // Silenciar errores de polling
      }
    }, 10000)

    return () => clearInterval(interval)
  }, [currentUser?.token])

  // Resetear showClosedInSearch cuando se limpia el searchTerm
  useEffect(() => {
    if (!searchTerm.trim()) {
      setShowClosedInSearch(false)
    }
  }, [searchTerm])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleBackToAdmin = () => {
    if (currentUser?.role === 'admin') {
      navigate('/admin')
    }
  }

  const handleTicketClick = async (ticket) => {
    // Navegar a la página de detalle del ticket
    navigate(`/ticket/${ticket.id}`)
  }

  // Mapear solicitudes a formato de tickets
  const tickets = requests.map(req => {
    const patient = patients.find(p => p.id === req.patient_id)
    const clinicNameFromReq = req.clinic_name
    const clinicNameFromPatient = patient?.clinic_name || patient?.clinic
    const finalClinicName = clinicNameFromReq || clinicNameFromPatient || 'Sin clínica'

    return {
      id: req.id,
      consecutive: req.consecutive_number || req.id,
      patientName: patient?.name || 'Desconocido',
      clinicName: finalClinicName,
      doctorName: req.doctor_name || req.doctorName || '-',
      templateType: req.template_type || req.templateType || 'Solicitud',
      arch: req.foot_side || req.footSide || '-',
            status: req.status === 'pending' ? 'Pendiente' :
              req.status === 'in_progress' || req.status === 'in_production' ? 'En Producción' :
              req.status === 'ready' ? 'Lista para Entregar' :
              req.status === 'warranty' ? 'Garantía' :
              req.status === 'delivered' ? 'Entregada' :
              req.status === 'cancelled' ? 'Cancelada' : 'Pendiente',
      date: req.created_at || req.createdAt,
      files: req.files ? (Array.isArray(req.files) ? req.files : JSON.parse(req.files)) : [],
      notes: [],
      internalNotes: req.observations || '',
      shoeSize: req.shoe_size || req.shoeSize,
      specs: req.specs || {}
    }
  })

  const pendingTickets = tickets.filter(t => t.status === 'Pendiente')
    const warrantyTickets = tickets.filter(t => t.status === 'Garantía')
  const inProductionTickets = tickets.filter(t => t.status === 'En Producción')
  const readyTickets = tickets.filter(t => t.status === 'Lista para Entregar')
  const completedTickets = tickets.filter(t => t.status === 'Entregada')

  // Filtrar tickets según el estado seleccionado
  const filteredByStatus = statusFilter === null 
    ? tickets.filter(t => t.status !== 'Entregada' && t.status !== 'Cancelada')
    : tickets.filter(t => t.status === statusFilter)

  // Filtrar por búsqueda (nombre paciente, clínica, doctor)
  const searchMatches = (t) => 
    t.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.clinicName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.doctorName.toLowerCase().includes(searchTerm.toLowerCase())

  // Si hay búsqueda activa
  let displayTickets = []
  if (searchTerm.trim()) {
    // Tickets activos que coinciden
    const activeMatches = filteredByStatus.filter(searchMatches)
    displayTickets = activeMatches

    // Si el usuario quiere ver cerrados, añadir tickets cerrados que coincidan
    if (showClosedInSearch) {
      const closedMatches = tickets.filter(t => 
        (t.status === 'Entregada' || t.status === 'Cancelada') && searchMatches(t)
      )
      displayTickets = [...displayTickets, ...closedMatches]
    }
  } else {
    // Sin búsqueda, mostrar solo activos
    displayTickets = filteredByStatus
  }

  // Ordenar por fecha
  const sortedTickets = [...displayTickets].sort((a, b) => {
    const dateA = new Date(a.date)
    const dateB = new Date(b.date)
    return sortByDate === 'newest' ? dateB - dateA : dateA - dateB
  })

  // Toggle de estado - si clickea el mismo estado, desmarca
  const handleStatusToggle = (status) => {
    setStatusFilter(statusFilter === status ? null : status)
  }

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <LucvanHeader
        title="Producción"
        user={currentUser}
        onLogout={handleLogout}
        onBack={handleBackToAdmin}
        showBack={currentUser?.role === 'admin'}
        notifications={notifications}
        unreadCount={unreadCount}
        onMarkAsRead={markAsRead}
        onClearAll={markAllAsRead}
        isConnected={isConnected}
      />

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                  <span className="sr-only">Cerrar</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-blue-900">Dashboard de Producción</h2>
            <p className="text-sm text-gray-600 mt-1">
              {statusFilter ? `Filtrando por: ${statusFilter}` : 'Mostrando todos los tickets activos'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm font-semibold text-gray-700">Ordenar por fecha:</label>
            <select 
              value={sortByDate}
              onChange={(e) => setSortByDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="newest">Más recientes primero</option>
              <option value="oldest">Más antiguos primero</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div 
            onClick={() => handleStatusToggle('Pendiente')}
            className={`bg-white p-6 rounded-xl shadow-md border-l-4 border-yellow-400 cursor-pointer transition ${
              statusFilter === 'Pendiente' ? 'ring-2 ring-yellow-400 bg-yellow-50' : 'hover:shadow-lg'
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-600 text-sm font-medium">Pendientes</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{pendingTickets.length}</p>
              </div>
              <div className="text-4xl">⏳</div>
            </div>
          </div>

          <div 
            onClick={() => handleStatusToggle('Garantía')}
            className={`bg-white p-6 rounded-xl shadow-md border-l-4 border-orange-500 cursor-pointer transition ${
              statusFilter === 'Garantía' ? 'ring-2 ring-orange-500 bg-orange-50' : 'hover:shadow-lg'
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-600 text-sm font-medium">Garantías</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{warrantyTickets.length}</p>
              </div>
              <div className="text-4xl">🔧</div>
            </div>
          </div>

          <div 
            onClick={() => handleStatusToggle('En Producción')}
            className={`bg-white p-6 rounded-xl shadow-md border-l-4 border-blue-500 cursor-pointer transition ${
              statusFilter === 'En Producción' ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:shadow-lg'
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-600 text-sm font-medium">En Producción</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{inProductionTickets.length}</p>
              </div>
              <div className="text-4xl">🔨</div>
            </div>
          </div>

          <div 
            onClick={() => handleStatusToggle('Lista para Entregar')}
            className={`bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-500 cursor-pointer transition ${
              statusFilter === 'Lista para Entregar' ? 'ring-2 ring-purple-500 bg-purple-50' : 'hover:shadow-xl'
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-600 text-sm font-medium">Lista para Entregar</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{readyTickets.length}</p>
              </div>
              <div className="text-4xl">📦</div>
            </div>
          </div>
        </div>

        {/* Tickets List */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="text-gray-500">Cargando solicitudes...</div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {statusFilter ? `Tickets: ${statusFilter}` : 'Tickets activos'} ({sortedTickets.length})
              </h3>
              {statusFilter !== null && (
                <button
                  onClick={() => setStatusFilter(null)}
                  className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
                >
                  Ver todos
                </button>
              )}
            </div>
            
            {/* Buscador */}
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar por paciente, clínica o doctor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg 
                className="absolute left-3 top-3 h-5 w-5 text-gray-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Botón para buscar en cerrados */}
            {searchTerm.trim() && (
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => setShowClosedInSearch(!showClosedInSearch)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                    showClosedInSearch 
                      ? 'bg-gray-700 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {showClosedInSearch ? '✓ Buscar en cerrados' : 'Buscar en cerrados'}
                </button>
              </div>
            )}
          </div>

          <div className="divide-y divide-gray-200">
            {sortedTickets.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {searchTerm 
                  ? `No se encontraron resultados para "${searchTerm}"${showClosedInSearch ? ' (incluyendo cerrados)' : ''}`
                  : statusFilter === 'all' 
                    ? 'No hay tickets registrados' 
                    : `No hay tickets con estado "${statusFilter}"`
                }
              </div>
            ) : (
              sortedTickets.map((ticket) => {
                const isClosed = ticket.status === 'Entregada' || ticket.status === 'Cancelada'
                return (
                <div 
                  key={ticket.id} 
                  onClick={() => handleTicketClick(ticket)}
                  className={`p-6 hover:bg-gray-50 transition cursor-pointer ${isClosed ? 'bg-gray-100 opacity-75' : ''}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`bg-[#003C63] text-white text-sm font-bold px-2 py-1 rounded ${isClosed ? 'line-through opacity-60' : ''}`}>#{ticket.consecutive || ticket.id}</span>
                        <h3 className={`text-lg font-bold text-gray-900 ${isClosed ? 'line-through opacity-60' : ''}`}>{ticket.patientName}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          ticket.status === 'Pendiente' ? 'bg-yellow-100 text-yellow-800' :
                          ticket.status === 'En Producción' ? 'bg-blue-100 text-blue-800' :
                          ticket.status === 'Lista para Entregar' ? 'bg-purple-100 text-purple-800' :
                          ticket.status === 'Garantía' ? 'bg-orange-100 text-orange-800' :
                          ticket.status === 'Entregada' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {ticket.status}
                        </span>
                      </div>
                      
                      <div className={`grid grid-cols-4 gap-4 text-sm text-gray-600 ${isClosed ? 'opacity-60' : ''}`}>
                        <div>
                          <span className="font-medium">Clínica:</span> {ticket.clinicName}
                        </div>
                        <div>
                          <span className="font-medium">Doctor:</span> {ticket.doctorName}
                        </div>
                        <div>
                          <span className="font-medium">Tipo:</span> {ticket.templateType}
                        </div>
                        <div>
                          <span className="font-medium">Arcada:</span> {ticket.arch}
                        </div>
                      </div>

                      <div className={`mt-2 text-sm text-gray-500 ${isClosed ? 'opacity-60' : ''}`}>
                        Fecha: {new Date(ticket.date).toLocaleDateString('es-ES')}
                        {ticket.files.length > 0 && ` • ${ticket.files.length} archivo(s) adjunto(s)`}
                      </div>
                    </div>

                    <div className={`text-blue-500 text-sm font-semibold ml-4 ${isClosed ? 'opacity-40' : ''}`}>
                      Ver detalles →
                    </div>
                  </div>
                </div>
              )})
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  )
}
